import type {
  AuthBootstrapRecoveryReason,
  AuthRedirectAppState,
} from '../../types/auth/auth-recovery.type';

const AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEYS: Record<
  AuthBootstrapRecoveryReason,
  string
> = {
  token: 'auth.bootstrap.recovery.token.attempted',
  'identity-linked': 'auth.bootstrap.recovery.identity-linked.attempted',
};
const LEGACY_AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEY =
  'auth.bootstrap.recovery.attempted';

const getSafeSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Returns the current in-app relative location so login flows can restore it.
 */
export const getCurrentReturnTo = (): string => {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

/**
 * Builds Auth0 app state for redirects initiated from the SPA.
 */
export const getAuthRedirectAppState = (): AuthRedirectAppState => ({
  returnTo: getCurrentReturnTo(),
});

/**
 * Resolves the location that should be restored after Auth0 redirects back.
 */
export const getAuthRedirectReturnTo = (appState?: AuthRedirectAppState) => {
  const returnTo = appState?.returnTo;

  return typeof returnTo === 'string' && returnTo.length > 0
    ? returnTo
    : window.location.pathname;
};

/**
 * Restores the SPA URL after Auth0 returns control to the browser.
 */
export const restoreAuthRedirectUrl = (
  appState?: AuthRedirectAppState
): void => {
  const returnTo = getAuthRedirectReturnTo(appState);
  const nextUrl = new URL(returnTo, window.location.origin);
  const currentUrl = new URL(window.location.href);

  if (currentUrl.pathname + currentUrl.search + currentUrl.hash !== returnTo) {
    window.history.replaceState({}, document.title, nextUrl.toString());
  }
};

/**
 * Indicates whether the app already attempted one interactive auth recovery
 * for the specified failure reason.
 *
 * @param reason - Recovery flow whose redirect budget should be checked.
 * @returns Whether the redirect budget was already consumed.
 */
export const hasAuthBootstrapRecoveryAttempted = (
  reason: AuthBootstrapRecoveryReason
): boolean => {
  return (
    getSafeSessionStorage()?.getItem(
      AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEYS[reason]
    ) === '1'
  );
};

/**
 * Marks that an interactive auth recovery redirect has already been attempted
 * for the specified failure reason.
 *
 * @param reason - Recovery flow whose redirect budget should be consumed.
 * @returns Nothing.
 */
export const markAuthBootstrapRecoveryAttempted = (
  reason: AuthBootstrapRecoveryReason
): void => {
  getSafeSessionStorage()?.setItem(
    AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEYS[reason],
    '1'
  );
};

/**
 * Clears every interactive auth recovery marker after logout or successful
 * bootstrap, including the marker used by previous frontend versions.
 *
 * @returns Nothing.
 */
export const clearAuthBootstrapRecoveryAttempts = (): void => {
  const storage = getSafeSessionStorage();

  for (const storageKey of Object.values(
    AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEYS
  )) {
    storage?.removeItem(storageKey);
  }
  storage?.removeItem(LEGACY_AUTH_BOOTSTRAP_RECOVERY_STORAGE_KEY);
};
